import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, UtensilsCrossed, Home as HomeIcon, ShieldCheck, Zap, Sparkles, Check, FileText } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { AppWalkthrough } from "@/components/tools/AppWalkthrough";

const printables = [
  {
    icon: BookOpen,
    name: "The Working Mom Weekly Reset™",
    price: "$17",
    desc: "The full 27-page weekly workbook — the complete RESET practice, fully fillable.",
  },
  {
    icon: UtensilsCrossed,
    name: "The Meal Planner",
    price: "$8",
    desc: "Plan the week's meals and quiet the daily what's-for-dinner decision fatigue.",
  },
  {
    icon: HomeIcon,
    name: "The Family Command Center",
    price: "$8",
    desc: "One calm hub for the calendar, responsibilities, and everything the household needs.",
  },
  {
    icon: ShieldCheck,
    name: "The Boundaries Worksheet",
    price: "$8",
    desc: "Name what matters, define the line, and protect your time and energy.",
  },
  {
    icon: Zap,
    name: "The 5-Minute Reset™",
    price: "Free",
    desc: "A five-minute taste of the practice — clear the mental load and reset your week.",
    free: true,
  },
];

const reset = [
  ["Reflect", "Honor the week you just lived."],
  ["Empty", "Release the mental load and pressure."],
  ["Select", "Choose the priorities that matter most."],
  ["Establish", "Create systems that support success."],
  ["Thrive", "Move forward intentionally."],
];

const appFeatures = [
  "All five tools, fillable and interactive",
  "Every entry saves automatically as you type",
  "Open it on any device, whenever you need it",
];

export default function Shop() {
  return (
    <main data-testid="shop-page">
      {/* HERO — RESET Method as the shared foundation */}
      <section className="pt-40 md:pt-52 pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-6">Resources</p>
            <h1 className="font-serif font-light text-wine text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight">
              Practical tools to lead your week with intention.
            </h1>
            <p className="mt-8 text-lg text-charcoal/80 leading-relaxed max-w-2xl">
              Practical tools to help you lead, decide, and move forward with greater intention — at work,
              at home, and within yourself. Built on the RESET Method™ — one repeatable weekly practice.
              Choose the format that fits your life: fully fillable digital PDFs, or the interactive app that
              fills in and saves as you go.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-5 gap-px bg-gold/30 border border-gold/30">
              {reset.map(([title, desc], i) => (
                <div key={title} className="bg-cream p-5">
                  <span className="font-serif text-3xl text-terracotta">{title[0]}</span>
                  <h3 className="font-serif text-lg text-wine mt-2">{title}</h3>
                  <p className="mt-1.5 text-xs text-charcoal/65 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* DIGITAL PDFs */}
      <section className="py-20 md:py-28 bg-blush/30" data-testid="printables-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">Format One</p>
            <h2 className="font-serif font-light text-wine text-4xl md:text-5xl leading-tight flex items-center gap-3">
              <FileText size={34} className="text-terracotta shrink-0" strokeWidth={1.5} />
              Digital PDFs — print or fill on any device
            </h2>
            <p className="mt-5 max-w-2xl text-charcoal/75 leading-relaxed">
              Each PDF is fully fillable — type directly into it on your phone, tablet, or computer, or print
              and fill by hand. Your choice. Buy only what you need — start with the free 5-Minute Reset™, or
              build your own toolkit.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {printables.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.name} delay={i * 0.06}>
                  <Link
                    to="/downloads"
                    data-testid={`printable-${i}`}
                    className="group flex flex-col h-full rounded-2xl border border-gold/40 bg-cream p-7 transition-colors hover:border-terracotta"
                  >
                    <div className="flex items-center justify-between">
                      <div className="w-12 h-12 rounded-full bg-blush/60 flex items-center justify-center">
                        <Icon size={20} className="text-wine" />
                      </div>
                      <span className={`font-serif text-2xl ${p.free ? "text-terracotta" : "text-wine"}`}>{p.price}</span>
                    </div>
                    <h3 className="mt-5 font-serif text-xl text-wine leading-snug">{p.name}</h3>
                    <p className="mt-3 text-sm text-charcoal/70 leading-relaxed flex-1">{p.desc}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-terracotta group-hover:text-wine transition-colors">
                      {p.free ? "Get it free" : "Buy & download"}
                      <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* INTERACTIVE APP */}
      <section className="py-20 md:py-28" data-testid="interactive-app-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">Format Two</p>
              <h2 className="font-serif font-light text-wine text-4xl md:text-5xl leading-tight">The Interactive App</h2>
              <p className="mt-5 text-lg text-charcoal/80 leading-relaxed max-w-xl">
                Want it all in one place, remembered for you? The same five tools — the Weekly Reset™, Meal
                Planner, Family Command Center, Boundaries Worksheet, and 5-Minute Reset™ — live in one
                interactive app that saves your answers automatically and adds every new tool as it launches.
              </p>
              <ul className="mt-8 space-y-4">
                {appFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <span className="mt-1 flex-none w-5 h-5 rounded-full bg-terracotta text-cream flex items-center justify-center">
                      <Check size={12} />
                    </span>
                    <span className="text-charcoal/85">{f}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-terracotta">
                <Sparkles size={14} /> Choose your access
              </p>
              <div className="mt-5 space-y-4 max-w-md">
                <Link
                  to="/tools"
                  data-testid="access-lifetime"
                  className="group block rounded-xl bg-cream border border-gold/40 p-6 transition-colors hover:border-terracotta"
                >
                  <div className="flex items-baseline justify-between">
                    <h3 className="font-serif text-xl text-wine">Lifetime Access</h3>
                    <span className="font-serif text-3xl text-wine">$29</span>
                  </div>
                  <p className="mt-2 text-sm text-charcoal/70">Own today&apos;s five tools forever. Pay once.</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-terracotta group-hover:text-wine transition-colors">
                    Sign up &amp; buy <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </Link>
                <Link
                  to="/tools"
                  data-testid="access-membership"
                  className="group block rounded-xl bg-wine text-cream p-6 transition-opacity hover:opacity-95"
                >
                  <div className="flex items-baseline justify-between">
                    <h3 className="font-serif text-xl">Membership</h3>
                    <span className="font-serif text-2xl">$5<span className="text-sm text-cream/70">/mo</span> · $45<span className="text-sm text-cream/70">/yr</span></span>
                  </div>
                  <p className="mt-2 text-sm text-cream/80">All five tools plus every future tool, automatically.</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-blush">
                    Sign up &amp; subscribe <ArrowRight size={13} className="transition-transform" />
                  </span>
                </Link>
              </div>
              <p className="mt-5 text-sm text-charcoal/60 max-w-md">
                Prefer paper? Get the{" "}
                <Link to="/downloads" data-testid="start-free-link" className="text-terracotta underline underline-offset-2 hover:text-wine">
                  5-Minute Reset™ as a free PDF
                </Link>
                .
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-6 flex justify-center">
            <Reveal delay={0.1}>
              <div>
                <p className="text-center text-xs uppercase tracking-[0.3em] text-terracotta mb-6" data-testid="app-preview-label">
                  A peek inside the app
                </p>
                <AppWalkthrough />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CLOSING — not sure? try the free 5-Minute Reset */}
      <section className="bg-terracotta text-cream py-20 md:py-28" data-testid="shop-closing">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="font-serif font-light text-3xl md:text-5xl leading-tight">
              Not sure which format is for you?
            </h2>
            <p className="mt-6 text-cream/85 text-lg leading-relaxed">
              Start with the free 5-Minute Reset™ — it&apos;s available as a fillable PDF and inside the app. Try
              the practice first, then choose the format that feels right.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/downloads"
                data-testid="closing-free-printable"
                className="rounded-full bg-cream text-terracotta px-8 py-4 text-sm font-medium tracking-wide hover:bg-charcoal hover:text-cream transition-colors"
              >
                Get the free PDF
              </Link>
              <Link
                to="/tools"
                data-testid="closing-free-app"
                className="rounded-full border border-cream/70 text-cream px-8 py-4 text-sm font-medium tracking-wide hover:bg-cream hover:text-terracotta transition-colors"
              >
                Try it in the app
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
